import {
  all,
  takeEvery,
  takeLatest,
  put,
  call,
  select,
} from 'redux-saga/effects';
import { startSubmit, stopSubmit } from 'redux-form';
import { push } from 'connected-react-router';
import { authActionTypes } from './Auth.actionTypes';
import authService from '../../services/auth';
import companyService from '../../services/company';
import {
  addNotification,
  notificationStates,
} from '../../components/Notification';
import { isEmpty } from 'lodash';
import { currentCompany } from '../../utils/utility';
import { getErrorMessage, logError } from '../../utils/errorUtils';
import {
  ensureLegacyMapLoaded,
  getEffectiveUserId,
} from '../../utils/legacyUserIdMap';
import { teamActionTypes } from '../Team/Team.actionTypes';
import { getPlanFeaturesAndModules } from '@/services/planFeatures';
import { planFeaturesActionTypes } from '../PlanFeatures/PlanFeatures.actionTypes';
import { isSignupEmailDomainBlocked } from '@/utils/authSignupEmail';
import { appRoutes } from '@/routes';
import { acceptInviteFromInvites } from '@/services/invitesClient';
import { IS_DEMO_MODE } from '@/utils/demoMode';

export function* loginSaga({ payload }) {
  yield put({
    type: authActionTypes.SET_STATE,
    payload: {
      loading: true,
    },
  });
  try {
    yield put(startSubmit('loginForm'));
    const response = yield call(
      authService.signInWithEmail,
      payload.email,
      payload.password
    );
    if (response) {
      yield put({
        type: authActionTypes.LOAD_CURRENT_ACCOUNT,
        payload: { action: 'signIn' },
      });
      yield call(ensureLegacyMapLoaded);
      const response = yield call(authService.currentAccount);
      const effectiveId = getEffectiveUserId(response.uid);
      let { success, data } = yield call(
        authService.getCurrentUserDetails,
        effectiveId
      );

      if (data.companyList.length === 1) {
        localStorage.setItem('teamData', []);
        yield put({
          type: teamActionTypes.SWITCH_COMPANY_ACTIVE_TEAM,
          payload: {
            companyId: data.companyList[0].companyId,
            userId: effectiveId,
          },
        });
      }
    }
  } catch (error) {
    addNotification({
      message: error.message,
      level: notificationStates.error,
    });
  }
  yield put(stopSubmit('loginForm'));
}

export function* loadCurrentAccountSaga(props) {
  // Demo mode: this saga is invoked unconditionally on every app boot (see
  // authWatcher below) to check the real Firebase session. The demo profile
  // has no real session/company doc, so it resolves to an empty
  // activeCompany and resets state.team (teams/activeTeam/selectedTeamID) to
  // empty a couple seconds after mount — wiping the header's Department pill
  // seeded in Team.reducer.js. Skip entirely; the demo profile/team state is
  // already seeded at init.
  if (IS_DEMO_MODE) return;

  yield put({
    type: authActionTypes.SET_STATE,
    payload: {
      loading: true,
    },
  });

  try {
    yield call(ensureLegacyMapLoaded);
    const response = yield call(authService.currentAccount);
    const cid = currentCompany();
    if (response) {
      const {
        uid: id,
        email,
        emailVerified,
        photoURL: avatar,
        displayName,
      } = response;
      const effectiveId = getEffectiveUserId(id);
      let { success, data } = yield call(
        authService.getCurrentUserDetails,
        effectiveId
      );
      if (!emailVerified && props?.payload?.action == 'signIn') {
        addNotification({
          message: 'Please verify your email address.',
          level: notificationStates.error,
        });
      }

      // Create license if email is verified and user doesn't have one
      if (emailVerified && success && data) {
        if (!data.encryptedLicenseKey) {
          // User is verified but doesn't have a license yet - create it
          try {
            console.log(
              'Email verified user without license detected, creating license:',
              id
            );
            const licenseResult = yield call(
              authService.createLicenseForVerifiedUser
            );
            if (licenseResult.success) {
              console.log(
                'License created successfully for verified user:',
                id
              );
              // Reload user details to get the new license key
              const updatedUserDetails = yield call(
                authService.getCurrentUserDetails,
                effectiveId
              );
              if (updatedUserDetails.success) {
                data = updatedUserDetails.data;
              }
            } else {
              console.warn('License creation failed:', licenseResult.data);
            }
          } catch (error) {
            // Log error but don't block the flow
            console.error('Error creating license for verified user:', error);
          }
        }
      }

      if (success) {
        // Single company: auto-select. Multiple: only valid localStorage `cid` (e.g. after
        // /select-company + SWITCH_COMPANY). We do not use membersDatabase activeCompanyId
        // to skip the picker — user must choose (or use cid from current session).
        const companyList =
          !isEmpty(data) && Array.isArray(data.companyList)
            ? data.companyList
            : [];
        let activeCompany = {};
        if (companyList.length > 0) {
          const byStored =
            !isEmpty(cid) &&
            companyList.find((i) => String(i.companyId) === String(cid));

          if (companyList.length === 1) {
            activeCompany = companyList[0];
            if (activeCompany?.companyId) {
              localStorage.setItem('cid', activeCompany.companyId);
            }
          } else {
            if (byStored) {
              activeCompany = byStored;
            } else {
              activeCompany = {};
              if (!isEmpty(cid)) {
                localStorage.removeItem('cid');
              }
            }
          }
        }

        if (!activeCompany?.companyId) {
          localStorage.removeItem('teamData');
          localStorage.removeItem('Team');
          yield put({
            type: teamActionTypes.SET_STATE,
            payload: {
              selectedTeamID: null,
              activeUserID: null,
              activeTeam: {},
              active: [],
              teams: [],
            },
          });
        }

        // NEW: Fetch subscription data for active company if it exists
        let subscriptionData = {};
        if (activeCompany && activeCompany.companyId) {
          try {
            console.log(
              'Subscription: LOAD_CURRENT_ACCOUNT - Fetching subscription data for company:',
              activeCompany.companyId
            );
            subscriptionData = yield call(
              companyService.getCompanySubscriptionStatus,
              activeCompany.companyId
            );
            console.log(
              'Subscription: LOAD_CURRENT_ACCOUNT - Fetched subscription data:',
              subscriptionData
            );
          } catch (error) {
            console.error(
              'Subscription: LOAD_CURRENT_ACCOUNT - Error fetching subscription data:',
              error
            );
            // Fallback to basic subscription data
            subscriptionData = {
              subscriptionStatus: 'trial',
              trialExpiryDate: null,
              subscriptionExpiryDate: null,
            };
          }
        }

        console.log(
          'Subscription: LOAD_CURRENT_ACCOUNT - Active company:',
          activeCompany
        );
        // Set basic company data and subscription data separately (profile.id = effectiveId for Firestore/data lookups)
        yield put({
          type: authActionTypes.SET_STATE,
          payload: {
            profile: {
              id: effectiveId,
              name: displayName || email,
              email,
              emailVerified,
              onboardingData: data?.onboardingData,
              setupGuideData: data?.setupGuideData,
              avatar,
              role: !isEmpty(data)
                ? data.companyList &&
                  data.companyList.length > 0 &&
                  data.companyList[0].role
                : {},
              companyList: !isEmpty(data) ? (data?.companyList ?? []) : [],
              activeCompany: activeCompany,
            },
            authorized: emailVerified ? true : false,
          },
        });
        console.log(
          'Subscription: LOAD_CURRENT_ACCOUNT - Subscription data:',
          subscriptionData
        );
        // Set subscription data separately
        if (Object.keys(subscriptionData).length > 0) {
          yield put({
            type: authActionTypes.SET_SUBSCRIPTION_DATA,
            payload: subscriptionData,
          });
        }
        yield put({
          type: planFeaturesActionTypes.SET_STATE,
          payload: {
            loading: true,
            error: null,
          },
        });
        const featuresData = yield call(getPlanFeaturesAndModules);
        const { allModulesAndFeatures, ...plans } = featuresData ?? {};
        yield put({
          type: planFeaturesActionTypes.SET_ALL_FEATURES_AND_MODULES,
          payload: allModulesAndFeatures,
        });
        yield put({
          type: planFeaturesActionTypes.SET_PLANS,
          payload: plans,
        });
        yield put({
          type: planFeaturesActionTypes.SET_STATE,
          payload: {
            loading: false,
            error: null,
          },
        });
      }
    }
  } catch (error) {
    console.error('Error in LOAD_CURRENT_ACCOUNT:', error);
    try {
      addNotification({
        message: 'Failed to load account details',
        level: notificationStates.error,
      });
    } catch (notificationError) {
      console.warn('Failed to show notification:', notificationError);
    }
  } finally {
    console.log('Subscription: LOAD_CURRENT_ACCOUNT - Finally');
    yield put({
      type: authActionTypes.SET_STATE,
      payload: {
        loading: false,
      },
    });
  }
}

export function* signUpSaga({ payload }) {
  yield put({
    type: authActionTypes.SET_STATE,
    payload: { signUp: { pending: true, success: false, error: false } },
  });

  let firebaseUser = null;
  let rollbackNeeded = false;

  try {
    // Input validation
    if (!payload || !payload.email || !payload.password || !payload.fullName) {
      throw new Error(
        'Please provide all required information: email, password, and full name.'
      );
    }

    if (isSignupEmailDomainBlocked(payload.email)) {
      throw new Error(
        'Sign up with this email domain is not allowed. Please use a different email address.'
      );
    }

    // Log signup attempt
    console.log('Signup attempt:', {
      email: payload.email,
      timestamp: new Date().toISOString(),
    });

    // Step 1: Create Firebase Auth user
    const response = yield call(
      authService.signUpWithEmailAndPassword,
      payload.email,
      payload.password
    );

    if (!response || !response.user) {
      throw new Error('Failed to create user account');
    }

    firebaseUser = response.user;
    rollbackNeeded = true;

    // Step 2: Execute all dependent operations in parallel
    const invitationData = yield select(
      (state) => state.invitation?.invitationData
    );
    // Comes from the sign-up form, not redux — redux invitationData can be
    // left over from an earlier visit and isn't reliable here.
    const isInviteSignup = !!(payload.inviteId && payload.inviteToken);

    const parallelCalls = [
      call(authService.sendVerificationEmail, {
        email: payload.email,
        fullName: payload.fullName,
      }),
      call(authService.createUser, {
        email: firebaseUser.email,
        uid: firebaseUser.uid,
        fullName: payload.fullName,
        invitationData,
      }),
      call(authService.updateProfileName, payload.fullName),
    ];
    if (isInviteSignup) {
      // If this fails, no big deal — the verification email above still
      // goes out, so the account never gets stuck.
      parallelCalls.push(
        call(authService.verifyInviteSignupEmail, {
          inviteId: payload.inviteId,
          inviteToken: payload.inviteToken,
        })
      );
    }

    const [
      verificationResult,
      createUserResult,
      profileResult,
      inviteVerifyResult,
    ] = yield all(parallelCalls);
    const inviteVerified =
      isInviteSignup && inviteVerifyResult?.success === true;

    // Step 3: Validate all operations succeeded. An invite-verified user
    // doesn't actually need the verification email to have gone out — don't
    // roll back a legitimate account over that email failing.
    const allSuccessful =
      (inviteVerified || verificationResult.success) &&
      createUserResult.success &&
      profileResult.success;

    if (!allSuccessful) {
      // Log the specific failures
      logError('SIGN_UP', new Error('Partial operation failure'), {
        verificationResult,
        createUserResult,
        profileResult,
        uid: firebaseUser.uid,
      });

      // Rollback: Delete both Firestore document and Firebase Auth user
      try {
        // Delete Firestore document if it was created
        if (createUserResult.success) {
          yield call(authService.deleteUserDocument, firebaseUser.uid);
          console.log(
            'Firestore document cleanup successful for user:',
            firebaseUser.uid
          );
        }

        // Delete Firebase Auth user
        yield call(authService.deleteUser, firebaseUser.uid);
        console.log(
          'Firebase Auth user cleanup successful for user:',
          firebaseUser.uid
        );
      } catch (rollbackError) {
        logError('SIGN_UP_ROLLBACK', rollbackError, { uid: firebaseUser.uid });
        // Continue with error handling even if rollback fails
      }

      rollbackNeeded = false;

      throw new Error('Failed to complete account setup. Please try again.');
    }

    if (inviteVerified) {
      // Don't clear pending until we're actually done — this next part
      // (loading the account) can take a few seconds, and the button
      // spinner should stay on so it doesn't look frozen.
      //
      // Reload the user first so emailVerified is up to date, then load
      // the account with `call` (not `put`) so we wait for it to finish —
      // otherwise the page could redirect before the user is authorized
      // and get bounced back to login.
      yield call(authService.reloadCurrentUser);

      // Create the license first (requires emailVerified) so the welcome
      // email below has a Member GUID to show instead of "N/A".
      yield call(loadCurrentAccountSaga, {
        type: authActionTypes.LOAD_CURRENT_ACCOUNT,
      });

      // Auto-accept the invite so the user isn't sent to the Invitations Hub
      // to accept manually. Falls back to the hub if this fails.
      const acceptResult = yield call(
        acceptInviteFromInvites,
        payload.inviteId,
        payload.inviteToken,
        firebaseUser
      );

      // Refresh the profile with the membership just granted.
      yield call(loadCurrentAccountSaga, {
        type: authActionTypes.LOAD_CURRENT_ACCOUNT,
      });

      // Navigate before clearing `pending` so the layout gate doesn't fire
      // its own redirect first (see isSignUpInFlight in layouts/index.js).
      const targetRoute = acceptResult?.success
        ? appRoutes().teamDashboard
        : appRoutes().invitationsHub;
      const successMessage = acceptResult?.success
        ? 'Account created and invitation accepted!'
        : 'Account created successfully!';

      yield put(push(targetRoute));
      yield put({
        type: authActionTypes.SET_STATE,
        payload: { signUp: { pending: false, success: true, error: false } },
      });
      yield put({ type: authActionTypes.RESET_SIGNUP_STATE });
      addNotification({
        message: successMessage,
        level: notificationStates.success,
        autoDismiss: 5,
      });
    } else {
      yield put({
        type: authActionTypes.SET_STATE,
        payload: { signUp: { pending: false, success: true, error: false } },
      });
      yield put({ type: authActionTypes.RESET_SIGNUP_STATE });
      yield put({ type: authActionTypes.LOAD_CURRENT_ACCOUNT });
      yield put(push(appRoutes().checkEmail));
      addNotification({
        message: 'Account created successfully! Please verify your email.',
        level: notificationStates.success,
        autoDismiss: 5,
      });
    }
  } catch (error) {
    // Log the error
    logError('SIGN_UP', error, {
      uid: firebaseUser?.uid,
      email: payload.email,
    });

    // Enhanced rollback if needed (for cases where rollback wasn't already attempted)
    if (rollbackNeeded && firebaseUser) {
      try {
        // Try to delete Firestore document first
        yield call(authService.deleteUserDocument, firebaseUser.uid);
        console.log(
          'Emergency Firestore cleanup successful for user:',
          firebaseUser.uid
        );

        // Then delete Firebase Auth user
        yield call(authService.deleteUser, firebaseUser.uid);
        console.log(
          'Emergency Firebase Auth cleanup successful for user:',
          firebaseUser.uid
        );
      } catch (rollbackError) {
        logError('SIGN_UP_EMERGENCY_ROLLBACK', rollbackError, {
          uid: firebaseUser.uid,
        });
        // Log for admin intervention - this is a critical issue
      }
    }

    yield put({
      type: authActionTypes.SET_STATE,
      payload: { signUp: { pending: false, success: false, error: true } },
    });
    yield put({ type: authActionTypes.RESET_SIGNUP_STATE });

    addNotification({
      message: getErrorMessage(error),
      level: notificationStates.error,
    });
  }
}

// export function* CREATE_USER({ payload }) {
//     setAuthState({ signUp: { pending: true, success: false, error: false } })
//     try {
//         const response = yield call(auth.createUser, { ...payload })
//         if (response) {
//             addNotification({
//                 message: "User created in Database",
//                 level: notificationStates.success
//             });
//         }

//     } catch (error) {
//         addNotification({
//             message: error.message,
//             level: notificationStates.error
//         });
//     }
// }

// export function* SEND_VERIFICATION_EMAIL() {
//     setAuthState({ signUp: { pending: true, success: false, error: false } })
//     try {
//         const response = yield call(auth.sendVerificationEmail)
//         if (response) {
//             addNotification({
//                 message: "Check your inbox!",
//                 level: notificationStates.success
//             });
//         }

//     } catch (error) {
//         addNotification({
//             message: error.message,
//             level: notificationStates.error
//         });
//     }
// }

// export function* SIGN_UP({ payload }) {
//     yield put({
//         type: "user/SET_STATE",
//         payload: { signUp: { pending: true, success: false, error: false } }
//     })
//     try {
//         const response = yield call(auth.signUpWithEmailAndPassword, payload.email, payload.password);
//         if (response) {
//             const [verified, updated] = yield all([
//                 call(auth.sendVerificationEmail),
//                 call(auth.updateProfileName, payload.fullName),
//             ])

//             if (verified && updated) {
//                 yield put({
//                     type: "user/SET_STATE",
//                     payload: { signUp: { pending: false, success: true, error: false } }
//                 })
//                 yield put({ type: 'user/RESET_SIGNUP_STATE' })
//                 yield put({ type: 'user/LOAD_CURRENT_ACCOUNT', })
//                 addNotification({
//                     message: "Account created! Please verify your account.",
//                     level: notificationStates.success
//                 });
//             }
//         }
//     } catch (error) {
//         yield put({
//             type: "user/SET_STATE",
//             payload: { signUp: { pending: false, success: false, error: true } }
//         })
//         yield put({ type: 'user/RESET_SIGNUP_STATE' })
//         addNotification({
//             message: error.message,
//             level: notificationStates.error
//         });

//     }
// }

export function* logoutActiveAccountSaga() {
  yield call(authService.signOut);
  localStorage.clear();
  yield put({
    type: authActionTypes.SET_STATE,
    payload: {
      authorized: false,
      profile: {
        subscription: {},
      },
    },
  });
  yield put({
    type: teamActionTypes.SET_STATE,
    payload: {
      loading: false,
      teamLoading: false,
      selectedTeamID: null,
      activeUserID: null,
      activeTeam: {},
      teamMembers: [],
      teams: [],
      teamLogs: [],
      active: [],
    },
  });
  yield put(push('/'));
}

export function* forgotPasswordSaga({ payload }) {
  yield put({
    type: authActionTypes.SET_STATE,
    payload: { loading: true },
  });

  try {
    const response = yield call(authService.sendPasswordResetEmail, {
      email: payload.email,
      fullName: payload.fullName,
    });
    if (response.success) {
      addNotification({
        message: 'Password reset email sent. Please check your inbox.',
        level: notificationStates.success,
      });
      yield put(push('/auth/login'));
    } else {
      addNotification({
        message: 'Failed to send reset email',
        level: notificationStates.error,
      });
    }
  } catch (error) {
    addNotification({
      message: 'Failed to send reset email',
      level: notificationStates.error,
    });
  } finally {
    yield put({
      type: authActionTypes.SET_STATE,
      payload: { loading: false },
    });
  }
}

export default function* authWatcher() {
  yield all([
    takeEvery(authActionTypes.LOGIN, loginSaga),
    takeEvery(authActionTypes.SIGN_UP, signUpSaga),
    takeEvery(authActionTypes.LOAD_CURRENT_ACCOUNT, loadCurrentAccountSaga),
    takeEvery(authActionTypes.FORGOT_PASSWORD, forgotPasswordSaga),
    takeLatest(authActionTypes.LOGOUT_ACTIVE_ACCOUNT, logoutActiveAccountSaga),
    loadCurrentAccountSaga(),
  ]);
}
